import { Camera, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../components/avatar';
import { ThemeToggle } from '../components/theme';
import { Button, Checkbox, ErrorBox, Field, Input, Notice, PageHeader, Panel, Pill, Select, useToast } from '../components/ui';
import { avatarDataUrl } from '../lib/image';
import { setLanguage } from '../i18n';
import { d } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';
import { authCall } from '../lib/rpc';

export function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const person = useQ('people.get', { id: me.person.id });
  const setLang = useM('me.setLanguage', { invalidate: ['me.'] });
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [pwError, setPwError] = useState<unknown>(null);
  const file = useRef<HTMLInputElement>(null);
  const setAvatar = useM('me.setAvatar', { invalidate: ['me.', 'people.'] });
  const removeAvatar = useM('me.removeAvatar', { invalidate: ['me.', 'people.'] });
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('profile.title')} subtitle={`${me.person.displayName} · ${me.person.email}`} />
      {__DEMO__ && <Notice tone="warn">{t('profile.demoNote')}</Notice>}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t('profile.picture')}>
          <div className="flex flex-wrap items-center gap-4">
            <Avatar personId={me.person.id} name={me.person.displayName} avatarAt={me.person.avatarAt} size={72} />
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                <Button icon={<Camera className="size-4" />} loading={setAvatar.isPending} onClick={() => file.current?.click()}>
                  {me.person.avatarAt ? t('profile.pictureChange') : t('profile.pictureUpload')}
                </Button>
                {me.person.avatarAt && (
                  <Button variant="ghost" icon={<Trash2 className="size-4" />} loading={removeAvatar.isPending} onClick={() => removeAvatar.mutateAsync({}).catch(toast.error)}>
                    {t('common.delete')}
                  </Button>
                )}
              </div>
              <span className="text-xs text-graphite">{t('profile.pictureHint')}</span>
            </div>
            <input
              ref={file}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                try {
                  await setAvatar.mutateAsync({ dataUrl: await avatarDataUrl(f) });
                  toast.ok(t('common.saved'));
                } catch (err) {
                  toast.error(err);
                }
              }}
            />
          </div>
        </Panel>
        <Panel title={t('nav.theme')}>
          <div className="flex flex-wrap items-center gap-3">
            <ThemeToggle />
            <span className="text-sm text-graphite">{t('profile.themeHint')}</span>
          </div>
        </Panel>
        <Panel title={t('profile.language')}>
          <div className="flex gap-2">
            {(['de', 'en', 'tr'] as const).map((l) => (
              <Button
                key={l}
                variant={i18n.language === l ? 'primary' : 'secondary'}
                onClick={() => {
                  setLanguage(l);
                  setLang.mutate({ language: l });
                }}
              >
                {l === 'de' ? 'Deutsch' : l === 'en' ? 'English' : 'Türkçe'}
              </Button>
            ))}
          </div>
        </Panel>
        {person.data && (
          <Panel title={t('people.contracts')}>
            <ul className="flex flex-col gap-1 text-sm">
              {person.data.contracts.slice(0, 3).map((c) => (
                <li key={c.id} className="num">
                  {d(c.validFrom)}: {t('people.weekly', { hours: c.weeklyHours })}
                </li>
              ))}
              <li className="text-graphite">
                {t('people.calendar')}: {person.data.person.holidayCalendar}
              </li>
              {Object.entries(person.data.absenceSummary?.days ?? {}).map(([k, v]) => (
                <li key={k}>
                  <Pill>{t(`week.absence.${k as 'vacation'}`)}</Pill> {t('people.days', { count: v })}
                </li>
              ))}
            </ul>
          </Panel>
        )}
        {me.person.kind === 'employee' && <BirthdayPanel />}
        <Panel title={t('profile.access')}>
          <p className="text-sm text-graphite">{t('profile.accessHint')}</p>
        </Panel>
        {!__DEMO__ && (
          <Panel title={t('profile.password')}>
            <form
              className="flex flex-col gap-3"
              onSubmit={async (e) => {
                e.preventDefault();
                setPwError(null);
                try {
                  await authCall('password', { current, next });
                  setCurrent('');
                  setNext('');
                  toast.ok(t('profile.changed'));
                } catch (err) {
                  setPwError(err);
                }
              }}
            >
              <Field label={t('profile.current')}>
                <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
              </Field>
              <Field label={t('profile.newPassword')} hint={t('auth.passwordRules')}>
                <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
              </Field>
              <ErrorBox error={pwError} />
              <Button type="submit" variant="primary" className="self-start" disabled={!current || next.length < 10}>
                {t('common.save')}
              </Button>
            </form>
          </Panel>
        )}
      </div>
    </div>
  );
}

/** Birthday in the team calendar: voluntary, day and month only, shown only when switched on. */
function BirthdayPanel() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const [md, setMd] = useState(me.person.birthday ?? '');
  const [show, setShow] = useState(me.person.showBirthday);
  const save = useM('me.setBirthday', { invalidate: ['me.', 'leave.team'] });
  const [m, dd] = md ? md.split('-') : ['', ''];
  const set = (month: string, day: string) => setMd(month && day ? `${month}-${day}` : '');
  const two = (n: number) => String(n).padStart(2, '0');
  return (
    <Panel title={t('leave.profileBirthday')}>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('common.day')}>
          <Select value={dd} onChange={(e) => set(m || '01', e.target.value)} className="w-24">
            <option value="">-</option>
            {Array.from({ length: 31 }, (_, i) => two(i + 1)).map((x) => (
              <option key={x} value={x}>
                {Number(x)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.month')}>
          <Select value={m} onChange={(e) => set(e.target.value, dd || '01')} className="w-24">
            <option value="">-</option>
            {Array.from({ length: 12 }, (_, i) => two(i + 1)).map((x) => (
              <option key={x} value={x}>
                {Number(x)}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox label={t('leave.profileShowBirthday')} checked={show && !!md} disabled={!md} onChange={(e) => setShow(e.target.checked)} className="pb-2" />
        <Button
          size="sm"
          loading={save.isPending}
          onClick={() =>
            save
              .mutateAsync({ birthday: md || null, show: !!md && show })
              .then(() => toast.ok(t('common.saved')))
              .catch(toast.error)
          }
        >
          {t('common.save')}
        </Button>
      </div>
      <p className="mt-2 text-xs text-graphite">{t('leave.profileBirthdayHint')}</p>
    </Panel>
  );
}
